"use strict";

import AppUtil from "/js/util/app-util.js";
import AnimSequencer from "/js/util/anim-sequencer.js";
import FadeUtil from "/js/util/fade-util.js";
import Strings from "/js/util/strings.js";
import CssUtil from "/js/util/css-util.js";
import FocusUtil from '../util/focus-util';

/** 
 * Image gallery overlay, which gets triggered by tapping on a gallery card thumbnail.
 *
 * An event sends the thumbnail view that was selected, which is then used to superimpose the
 * gallery view's image on top of it, and then animates the gallery view image to full size.
 *
 * Rem, when view is shown, `App` shows global close button, which can be used
 * to close the view.
 */
export default class GalleryView {

  #$el = $('#gallery');
  #$image = this.#$el.find('#galleryImage');

  #$cardThumb = null;
  #thumbImageRect = null;
  #thumbClipRect = null;

  constructor() {
    this.#$el.on("click tap", e => this.#onThisClick(e));
  }

  show(thumb) {
    this.#$el.css("visibility", "visible");

    this.#$cardThumb = thumb.$el;

    // Populate image.
    const url = thumb.$image.attr("src");
    this.#$image.attr("src", url);

    // Cache thumbnail's image rect.
    this.#thumbImageRect = thumb.$image[0].getBoundingClientRect();

    // Cache thumbnail's "cliprect".
    this.#thumbClipRect = thumb.$el[0].getBoundingClientRect();
    this.#thumbClipRect.x -= this.#thumbImageRect.x;
    this.#thumbClipRect.y -= this.#thumbImageRect.y;

    AppUtil.updateBodyClasses("lesson", "gallery");

    this.#animateIn();
  }

  get $el() {
    return this.#$el;
  }

  resize() {
    if (!CssUtil.isDisplayBlock(this.#$el)) {
      return;
    }
    AppUtil.setCssWithoutTransition(this.#$image, () => { this.#sizeImage(true); } );
  }

  /** Animates-in selected image from thumbnail size to full size. */
  #animateIn() {

    this.#$el.show();

    // Must wait a frame, for reasons.
    this.#$el.css("background-color", `rgba(0, 0, 0, 0.01)`);
    setTimeout(() => {
        // Tween background color.
        this.#$el.css("background-color", `rgba(0, 0, 0, 1.0)`);
        this.#$cardThumb.css("visibility", "hidden");
        },16);

    // Size to thumbnail, synchronous.
    AppUtil.setCssWithoutTransition(this.#$image, () => { this.#sizeImage(false); } );

    // Size to full, animated.
    AnimSequencer.runOne(this.#$image, 
      () => { this.#sizeImage(true); },
      () => { });
  }

  animateOut() {
    AppUtil.updateBodyClasses("lesson", null);

    // Background color.
    const col = `rgba(0, 0, 0, 0.0)`;
    this.#$el.css("background-color", col);

    // Shrink back to thumbnail.
    // TODO: Discontinuity when window has resized after animateIn. Must recalc thumbImageRect and/or thumbClipRect or smth.
    AnimSequencer.runOne(this.#$image, 
       () => { this.#sizeImage(false); },
       () => {
            this.#$el.hide();
            this.#$cardThumb.css("visibility", "visible");
        }
    );
  }

  #sizeImage(fullNotThumb=true) {
    if (!this.#thumbImageRect) {
      return;
    }

    const imageRect = fullNotThumb
        ? this.#calcImageRect() 
        : this.#thumbImageRect;
    GalleryView.#applyCssUsingRect(this.#$image, imageRect); 
  
    const clipRect = fullNotThumb
        ? { x: 0, y: 0, width: this.#$el.width(), height: this.#$el.height() }
        : this.#thumbClipRect;
    const s = GalleryView.#rectToPolygonString(clipRect);
    this.#$image.css("clip-path", s);
  }

  #calcImageRect() {
    const arImage = this.#thumbImageRect.width / this.#thumbImageRect.height;
    const arThis = this.#$el.width() / this.#$el.height();
    const rect = {};
    if (arThis > arImage) {
      rect.height = this.#$el.height();
      rect.width = this.#$el.height() * arImage;
    } else {
      rect.width = this.#$el.width();
      rect.height = this.#$el.width() / arImage;
    }
    rect.x = (this.#$el.width() - rect.width) / 2;
    rect.y = (this.#$el.height() - rect.height) / 2;
    return rect;
  }

  #onThisClick(e) {
    if (e.target === this.#$image[0]) {
      // Ignore click on image itself.
      return;
    }
    this.#$el.trigger("gallery-close");
  }

  static #applyCssUsingRect($el, rect) {
    $el.css("left", rect.x + "px");
    $el.css("top", rect.y + "px");
    $el.width(rect.width + "px");
    $el.height(rect.height + "px");
  }

  static #rectToPolygonString(rect) {
    let x1 = rect.x;
    let x2 = rect.x + rect.width;
    let y1 = rect.y;
    let y2 = rect.y + rect.height;
    const s = `polygon(${x1}px ${y1}px, ${x2}px ${y1}px, ${x2}px ${y2}px, ${x1}px ${y2}px)`;
    return s;
  }
}
